/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Form, Row, Space, Tabs, Tooltip, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import BotDeploymentsGeneralForm from './DeploymentSettings/BotDeploymentsGeneralForm';
import BotDeploymentsFacebookLayoutForm from './DeploymentSettings/Facebook/BotDeploymentsFacebookLayoutForm';
import { FacebookMessengerIcon } from '../Icons/platformIcons/FacebookMessenger';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { DEFAULT_DATETIME_TIMEZONE_FORMAT } from '../../constants/main';
import { transformDeleteMessengerPayload, transformToMessengerPayload } from '../../utils/transform/messengerProfile';
import { useFetchMessengerProfileQuery, useUpdateMessengerProfileMutation, useDeleteMessengerProfileMutation } from '../../store/botDeployments/messengerProfileApi';
import { isEmptyObject } from '../../utils/is/isEmptyObject';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import { useDeploymentFormErrors } from '../../utils/hooks/useDeploymentFormErrors';

// Constant declarations
const CALL_TO_ACTIONS = {
  POSTBACK: 'Postback',
  WEB_URL: 'Web URL'
};

const BotDeploymentsFacebookForm = ({
  formData={},
  onFormSubmit= () => {},
  userRoles={},
  isCreateMode=false,
  isUpdating=false,
  isCreating=false,
  handleBackButton= () => {},
  onPreviewIconClick = () => {},
  onDeleteClick = () => {},
  bodyClassName = '',
  footerClassName = '',
  ...restProps
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);
  const {
    errorFields,
    generateTabLabel,
    markAsErroneous,
    setErrorFields
  } = useDeploymentFormErrors();

  const initialValues = useMemo(() => {
    return isCreateMode ? {} : {
      ...formData,
      hideTypingIndicator: !formData.settings?.layout?.facebook?.showTypingIndicator
    };
  }, [formData]);

  const { data: messengerProfile, isError: fetchMessengerProfileError, isLoading: isLoadingMessengerProfile } = useFetchMessengerProfileQuery({ accessToken: formData.settings?.integration?.facebook?.pageAccessToken }, { skip: !formData.settings?.integration?.facebook?.pageAccessToken });
  const [updateMessengerProfile] = useUpdateMessengerProfileMutation();
  const [deleteMessengerProfile] = useDeleteMessengerProfileMutation();

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  useEffect(() => {
    // Map messenger profile data to form items
    if (!isCreateMode && messengerProfile?.data?.[0]) {
      const { get_started, greeting, persistent_menu } = messengerProfile.data[0];
      const transformedCallToActions = persistent_menu?.[0]?.call_to_actions?.map(action => {
        const transformedAction = {
          type: action.type.toUpperCase(),
          title: action.title
        };
        if (action.type.toUpperCase() === Object.keys(CALL_TO_ACTIONS)[0]) {
          transformedAction.data = JSON.parse(action.payload)?.data;
        } else {
          transformedAction.url = action.url;
        }
        return transformedAction;
      });

      let getStartedValue;
      try {
        getStartedValue = JSON.parse(get_started.payload);
      } catch {
        getStartedValue = null;
      }

      form.setFieldsValue({
        get_started: getStartedValue ? getStartedValue?.data : null,
        greeting: greeting?.[0]?.text,
        call_to_actions: transformedCallToActions || []
      });
    }
  }, [isCreateMode, messengerProfile]);

  // Form handlers
  const onFormSave = async (values) => {
    setErrorFields([]);
    const { get_started, greeting, call_to_actions, ...rest } = values;
    const valuesToSubmit = {
      ...rest
    };

    valuesToSubmit['settings'] = {
      ...valuesToSubmit['settings'],
      layout: {
        facebook: {
          showTypingIndicator: !valuesToSubmit['hideTypingIndicator']
        }
      }
    };

    delete valuesToSubmit['hideTypingIndicator'];

    if (isCreateMode) {
      valuesToSubmit['platform'] = 'FACEBOOK';
      valuesToSubmit['scope'] = 'USER';
    }

    const messengerProfileValues = transformToMessengerPayload({ get_started, greeting, call_to_actions });
    const deletedMessengerProfileValues = transformDeleteMessengerPayload(messengerProfile, { get_started, greeting, call_to_actions });

    if (!isEmptyObject(deletedMessengerProfileValues)) {
      await deleteMessengerProfile({ accessToken: formData.settings?.integration?.facebook?.pageAccessToken, data: deletedMessengerProfileValues });
    }
    if (!isEmptyObject(messengerProfileValues)) {
      await updateMessengerProfile({ accessToken: formData.settings?.integration?.facebook?.pageAccessToken, data: messengerProfileValues });
    }
    onFormSubmit(valuesToSubmit, formData.id, isCreateMode);
  };

  const onPreviewButtonClick = (deployment) => {
    onPreviewIconClick(deployment);
  };

  const onFieldError = (fields) => {
    setErrorFields(fields.errorFields || []);

    if (!isEmptyArray(fields.errorFields)) {
      form.scrollToField(fields.errorFields[0].name, { scrollMode: 'if-needed', behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        <Form
          name="facebookDeploymentForm"
          layout="vertical"
          onFinish={onFormSave}
          onFinishFailed={onFieldError}
          form={form}
          initialValues={initialValues}
          {...restProps}
        >
          <Row>
            <Col flex="auto">
              <Typography.Title level={4} className="mb-2">
                <Space>
                  <FacebookMessengerIcon />
                  {isCreateMode ? t('Facebook Messenger Deployment') : `${formData.displayName}`}
                </Space>
              </Typography.Title>
            </Col>

            {
              !isCreateMode ? (
                <Col flex="none">
                  <Space>
                    <Tooltip title={t('Preview the deployment')}>
                      <Button
                        size="large"
                        icon={<EyeOutlined />}
                        onClick={() => onPreviewButtonClick(formData)}
                      />
                    </Tooltip>
                  </Space>
                </Col>
              ) : null
            }
          </Row>

          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: generateTabLabel(t('General Settings'), '1'),
                forceRender: true,
                children: <BotDeploymentsGeneralForm
                  form={form}
                  formData={formData}
                  isCreateMode={isCreateMode}
                  onDeleteClick={onDeleteClick}
                  userRoles={userRoles}
                  description={t('Give a name and description to your deployment to make it easy to recognize. This is only for internal purposes.')}
                  hasErrors={errorFields.length}
                  shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, '1')}
                  fetchMessengerProfileError={fetchMessengerProfileError}
                  isLoadingMessengerProfile={isLoadingMessengerProfile}
                />
              },
              {
                key: '2',
                label: t('Layout Settings'),
                forceRender: true,
                children: <BotDeploymentsFacebookLayoutForm
                  form={form}
                  formData={formData}
                  isCreateMode={isCreateMode}
                  userRoles={userRoles}
                />
              }
            ]}
          />

        </Form>
      </div>
      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            {
              errorFields.length ? <p className="text-error">The form contains errors. Please review each tab to correct them.</p> : null
            }
          </Col>
        </Row>

        <Space className="d-flex justify-content-between">
          <Typography.Paragraph
            ellipsis
            className={classNames('text-secondary fs-sm mb-0', { 'opacity-0': isCreateMode })}
          >
            {
              !isCreateMode ? (
                <Fragment>
                  <span className="fw-bold">{t('Created')}:</span> {moment(formData.created).tz(formData.tenant.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT) }<br/>
                  <span className="fw-bold">{t('Last Modified')}:</span> {formData.lastChangedBy?.name ? `${formData.lastChangedBy.name}` : null} {moment(formData.lastChanged).tz(formData.tenant.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}<br/>
                </Fragment>
              ) : null
            }
          </Typography.Paragraph>

          <Row justify="end" gutter={8}>
            <Col>
              <Button
                size="large"
                onClick={handleBackButton}
                disabled={isUpdating || isCreating}
              >
                {t('Cancel')}
              </Button>
            </Col>

            <Col>
              <Button
                loading={isUpdating || isCreating}
                hidden={!hasEditAccess}
                className="float-right"
                type="primary"
                size="large"
                onClick={ form.submit }
              >
                {isCreateMode ? t('Create Deployment') : t('Save Changes')}
              </Button>
            </Col>
          </Row>
        </Space>
      </div>
    </Fragment>
  );
};

BotDeploymentsFacebookForm.propTypes = {
  formData: PropTypes.object,
  onFormSubmit: PropTypes.func,
  userRoles: PropTypes.object,
  isCreateMode: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isCreating: PropTypes.bool,
  handleBackButton: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onPreviewIconClick: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default BotDeploymentsFacebookForm;
